import moment from "moment";
import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { DATE_TIME_FORMAT_TYPES } from "../../../configs/constants/DateTimeFormatTypes";
import { routes } from "../../../configs/constants/routes";
import Invoice, { InvoiceRow } from "../../../models/Invoice";
import { customerRoute, formatRoute } from "../../../utils/route.utils";
import { formatCurrencyString } from "../../../utils/string.utils";
import AppIcon from "../../Core/AppIcon/AppIcon";
import AppInput from "../../Core/AppInput/AppInput";
import Button from "../../Core/Button/Button";
import Tag from "../Tag/Tag";
import Address from "../../../models/Address";
import Customer from "../../../models/Customer";
import { useInvoiceTemplates } from "../../../networking/invoice-template.hooks";

type EditableRow = {
	description: string;
	quantity: string;
	priceExcludingVAT: string;
	vatPercentage: number;
	originalRow?: InvoiceRow;
	rowIndex: number;
	editing?: boolean;
};

const emptyRow: EditableRow = {
	description: "",
	quantity: "0",
	priceExcludingVAT: "0",
	vatPercentage: 0,
	rowIndex: -1,
	editing: false,
};
interface InvoiceViewerProps {
	invoice: Invoice;
	editable?: boolean;
	onSubmit?: (
		invoiceRows: Pick<
			InvoiceRow,
			"description" | "priceExcludingVAT" | "quantity" | "vatPercentage"
		>[]
	) => void;
}

const InvoiceViewer: React.FC<InvoiceViewerProps> = ({ invoice, editable, onSubmit }) => {
	const { t } = useTranslation();

	// Resolve the template name for the "Mall: <name> v<version>" badge; the
	// id is the fallback for templates this list does not know about.
	const { templates } = useInvoiceTemplates(true, !!invoice.template);
	const templateLabel = invoice.template
		? `${t("invoice.template")}: ${
				templates.find((tpl) => tpl.id === invoice.template?.id)?.name ?? invoice.template.id
		  } v${invoice.template.version}`
		: undefined;

	const [customInvoiceRows, setCustomInvoiceRows] = useState<EditableRow[]>(
		(invoice.customInvoiceRows || []).map(createdEditableRowFromInvoiceRow)
	);

	function handleAddRow() {
		const newRow: EditableRow = {
			...emptyRow,
			editing: true,
			rowIndex: customInvoiceRows.length,
		};
		setCustomInvoiceRows([...customInvoiceRows, newRow]);
	}

	function handleSubmitRow() {
		const editingRow = getEditingRow();

		if (editingRow) {
			const newRow: InvoiceRow = {
				description: editingRow.description,
				priceExcludingVAT: Number(editingRow.priceExcludingVAT),
				quantity: Number(editingRow.quantity),
				vatPercentage: Number(editingRow.vatPercentage) * 100,
				total: Number(editingRow.priceExcludingVAT) * Number(editingRow.quantity), // TODO: This is prob wrong
				totalExcludingVat: Number(editingRow.priceExcludingVAT) * Number(editingRow.quantity), // TODO: This is prob wrong
				// priceExcludingVAT:
				// 	(Number(editingRow.price) * (100 - Number(editingRow.vatPercentage))) / 100,
			};

			const updatedRows = customInvoiceRows.map((row) => {
				if (row.editing) {
					row = editingRow;
					row.editing = false;
					row.originalRow = newRow;
				}
				return row;
			});

			setCustomInvoiceRows(updatedRows);

			onSubmit &&
				onSubmit(
					updatedRows.map((row) => ({
						description: row.originalRow!.description,
						priceExcludingVAT: row.originalRow!.priceExcludingVAT,
						quantity: row.originalRow!.quantity,
						vatPercentage: (row.originalRow!.vatPercentage || 0) / 100,
					}))
				);
		}
	}

	function handleCancel() {
		const editingRow = getEditingRow();

		if (editingRow && editingRow.originalRow) {
			// Revert back to old value when cancel if pressed if such exist
			customInvoiceRows[editingRow.rowIndex] = createdEditableRowFromInvoiceRow(
				editingRow.originalRow,
				editingRow.rowIndex
			);
			setCustomInvoiceRows([...customInvoiceRows]);
		} else {
			// Remove any non submitted new rows
			setCustomInvoiceRows(customInvoiceRows.filter((row) => !row.editing));
		}
	}

	function handleToggleEditRow(i: number) {
		customInvoiceRows[i].editing = true;
		setCustomInvoiceRows([...customInvoiceRows]);
	}

	const getEditingRow = useCallback(() => {
		return customInvoiceRows.find((row) => row.editing);
	}, [customInvoiceRows]);

	function updateEditingRow(data: Partial<EditableRow>) {
		setCustomInvoiceRows(
			customInvoiceRows.map((row) => {
				if (row.editing) {
					row = { ...row, ...data };
				}
				return row;
			})
		);
	}

	function handleDelete() {
		const editingRow = getEditingRow();

		if (editingRow) {
			if (window.confirm(t("invoice.confirmDeleteRow"))) {
				customInvoiceRows.splice(editingRow.rowIndex, 1);
				setCustomInvoiceRows([...customInvoiceRows]);

				onSubmit &&
					onSubmit(
						customInvoiceRows.map((row) => ({
							description: row.originalRow!.description,
							price: row.originalRow!.price,
							quantity: row.originalRow!.quantity,
							vatPercentage: row.originalRow!.vatPercentage,
						}))
					);
			}
		}
	}

	const editingRowTotal = useMemo(() => {
		const editingRow = getEditingRow();
		return editingRow ? Number(editingRow.priceExcludingVAT) * Number(editingRow.quantity) : 0;
	}, [getEditingRow]);

	function navigateToTimeReports(row: InvoiceRow) {
		if (row.job?.id) {
			// Grouped template rows link several time reports; highlight the first
			// (the page only highlights one) and let the job filter show the rest.
			const timeReportId = row.timeReport?.id || row.timeReports?.[0]?.id || "";
			const win = window.open(
				`${routes.TIME_REPORTS}?job=${JSON.stringify({ key: row.job.id })}&highlight=${timeReportId}`,
				"_blank"
			);
			win?.focus();
		}
	}

	function navigateToCustomer() {
		const win = window.open(customerRoute(invoice.customer.id), "_blank");
		win?.focus();
	}

	function renderEditableRow(key: number) {
		const editingRow = customInvoiceRows.find((row) => row.editing);

		if (!editingRow) return null;

		return (
			<tr key={key}>
				<td>
					<AppInput
						containerMargin="0"
						value={editingRow.description}
						placeholder="DESCRIPTION"
						onChangeText={(description) => updateEditingRow({ description })}
						onEnter={handleSubmitRow}
					/>
				</td>
				<td>
					<AppInput
						type="number"
						containerMargin="0"
						value={editingRow.quantity}
						placeholder="invoice.quantity"
						onChangeText={(quantity) => updateEditingRow({ quantity })}
						selectOnFocus
						onEnter={handleSubmitRow}
					/>
				</td>
				<td>
					<AppInput
						type="number"
						containerMargin="0"
						value={editingRow.priceExcludingVAT}
						placeholder="invoice.price"
						onChangeText={(price) => updateEditingRow({ priceExcludingVAT: price })}
						selectOnFocus
						onEnter={handleSubmitRow}
					/>
				</td>
				<td>
					<div className="col-with-del">
						<AppInput
							type="number"
							containerMargin="0"
							value={(editingRow.vatPercentage || 0) * 100}
							placeholder="%"
							onChangeText={(vatPercentage) =>
								updateEditingRow({ vatPercentage: Number(vatPercentage) / 100 })
							}
							selectOnFocus
							onEnter={handleSubmitRow}
						/>
						<div className="icon" onClick={handleDelete}>
							<AppIcon icon="red-cross" />
						</div>
					</div>
				</td>
				<td>{formatCurrencyString(editingRowTotal)}</td>
			</tr>
		);
	}

	const getAddressLine = (address?: Address): string => {
		if (address) {
			const { address1, address2, postalCode, city, country } = address;
			return [address1, address2, postalCode, city, country].filter((item) => item).join(", ");
		}
		return "";
	};

	return (
		<StyledInvoiceViewer>
			{invoice.credit && (
				<Tag className="credit" size="s" color="danger" text={t("invoice.credit.credit")}></Tag>
			)}
			{invoice.creditInvoiceReference && (
				<Tag className="credit" size="s" color="danger" text={t("invoice.credited")}></Tag>
			)}
			{templateLabel && <Tag className="credit" size="s" color="dividerGray" text={templateLabel}></Tag>}
			<div className="header-ctn">
				<table className="summary">
					<tbody>
						<tr>
							<th>{t("invoice.customer")}:</th>
							<td className="has-link" onClick={() => navigateToCustomer()}>
								{(invoice.customer as Customer).name}
							</td>
						</tr>
						<tr>
							<th>{t("invoice.invoiceNumber")}:</th>
							<td>{`${invoice.invoiceNumber}${
								invoice.status === "PREVIEW" ? " (preliminärt)" : ""
							}`}</td>
						</tr>
						{invoice.internalInvoiceNumber && invoice.internalInvoiceNumber !== invoice.invoiceNumber && (
							<tr>
								<th>{t("invoice.internalInvoiceNumber")}:</th>
								<td>{invoice.internalInvoiceNumber}</td>
							</tr>
						)}
						<tr>
							<th>{t("invoice.invoiceDate")}:</th>
							<td>{moment(invoice.invoiceDate).format(DATE_TIME_FORMAT_TYPES.DATE)}</td>
						</tr>
						<tr>
							<th>{t("invoice.dueDate")}:</th>
							<td>{moment(invoice.dueDate).format(DATE_TIME_FORMAT_TYPES.DATE)}</td>
						</tr>
						<tr>
							<th>{t("invoice.yourReference")}:</th>
							<td>{invoice.yourReference || "-"}</td>
						</tr>
						{invoice.costCenter && (
							<tr>
								<th>{t("invoice.costCenter")}:</th>
								<td>{invoice.costCenter}</td>
							</tr>
						)}
						<tr>
							<th>{t("invoice.address")}:</th>
							<td>{getAddressLine((invoice.customer as Customer)?.visitingAddress)}</td>
						</tr>
					</tbody>
				</table>

				<div className="address">
					<br />
					{invoice.address.address1} <br />
					{invoice.address.address2 ? (
						<>
							{invoice.address.address2}
							<br />
						</>
					) : (
						""
					)}
					{invoice.address.postalCode} {invoice.address.city}
				</div>
			</div>

			<table className="rows">
				<thead>
					<tr>
						<th className="description" style={{ width: "45%" }}>
							{t("invoice.description")}
						</th>
						<th style={{ width: "10%" }}>{t("invoice.quantity")}</th>
						<th style={{ width: "15%" }}>{t("invoice.price")}</th>
						<th style={{ width: "10%" }}>{t("invoice.vat")}</th>
						<th>{t("invoice.sum")}</th>
					</tr>
				</thead>
				<tbody>
					{invoice.invoiceRows.map((row, i) => (
						<tr key={i}>
							<td
								onClick={() => navigateToTimeReports(row)}
								className={row.job ? "has-link" : ""}
								title={
									row.timeReports && row.timeReports.length > 1
										? `${row.timeReports.length} ${t("invoice.timeReportsOnRow")}`
										: undefined
								}
							>
								{row.description.split("\n").map((row, i) => (
									<div key={i}>{row}</div>
								))}
							</td>
							<td>{formatPrice(row.quantity, false)}</td>
							<td>{formatPrice(row.priceExcludingVAT, false)}</td>
							<td>{row.vatPercentage !== undefined ? `${row.vatPercentage * 100}%` : ""}</td>
							<td>{formatPrice(row.totalExcludingVat, false)}</td>
						</tr>
					))}
					{customInvoiceRows.map((row, i) => {
						if (row.editing) {
							return renderEditableRow(i);
						} else {
							// Only clickable (to edit/delete) when the viewer is editable
							return (
								<tr
									key={i}
									className={editable ? "custom-invoice-row" : undefined}
									onClick={editable ? () => handleToggleEditRow(i) : undefined}
								>
									<td>{row.description}</td>
									<td>{row.quantity}</td>
									<td>{formatPrice(Number(row.priceExcludingVAT), false)}</td>
									<td>{(row.vatPercentage || 0) * 100}%</td>
									<td>
										{formatPrice(Number(row.priceExcludingVAT) * Number(row.quantity), false)}
									</td>
								</tr>
							);
						}
					})}

					{editable && (
						<tr>
							<td colSpan={5}>
								<div className="edit-row-btn-ctn">
									{!getEditingRow() ? (
										<Button
											margin="0.5rem 0 1rem 0"
											onClick={handleAddRow}
											backgroundColor="transparent"
											textColor="primary"
										>
											+ {t("ADD_ROW")}
										</Button>
									) : (
										<div className="save-btn-ctn">
											<Button
												margin="0.5rem 0 1rem 0"
												onClick={handleCancel}
												backgroundColor="transparent"
												textColor="grayTextDark"
											>
												{t("CANCEL")}
											</Button>
											<Button margin="0.5rem 0 1rem 0" onClick={handleSubmitRow}>
												{t("SAVE")}
											</Button>
										</div>
									)}
								</div>
							</td>
						</tr>
					)}
				</tbody>
				<tfoot>
					<tr>
						<td colSpan={10}></td>
					</tr>
					<tr className="total-row">
						<td colSpan={5}>
							<div>
								<div className="total-item">
									<div className="total-hd">{t("invoice.exclVat").toUpperCase()}</div>
									<div className="total-value">
										{formatPrice(invoice.total - invoice.totalVAT, true)}
									</div>
								</div>

								<div className="total-item">
									<div className="total-hd">{t("invoice.vat").toUpperCase()}</div>
									<div className="total-value">{formatPrice(invoice.totalVAT, true)}</div>
								</div>

								<div className="total-item to-pay">
									<div className="total-hd">{t("invoice.toPay").toUpperCase()}</div>
									<div className="total-value">{formatPrice(invoice.total, true)}</div>
								</div>
							</div>
						</td>
					</tr>
				</tfoot>
			</table>
		</StyledInvoiceViewer>
	);
};

export default InvoiceViewer;

const StyledInvoiceViewer = styled.div`
	table {
		border-collapse: collapse;

		th {
			font-weight: ${({ theme }) => theme.fontWeight.FONT_WEIGHT_MEDIUM};
		}
	}
	.credit {
		margin-bottom: 0.5rem;
	}

	.header-ctn {
		display: flex;
		justify-content: space-between;
		margin: 0 0 3rem 0;

		.summary {
			max-width: 600px;
			text-align: left;
			td,
			th {
				padding: 0.25rem 0.25rem 0.25rem 0;
			}
		}

		.address {
			line-height: 130%;
			min-width: 250px;
		}
	}

	.has-link {
		text-decoration: underline;
		cursor: pointer;
		color: ${(props) => props.theme.colors.COLOR_ACTION_BLUE};
	}

	.rows {
		width: 100%;

		thead th {
			padding: 0.45rem 0;
			border: 1px solid black;
			border-width: 1px 0;
			text-align: left;

			&:last-child {
				text-align: right;
			}
		}

		tbody {
			min-height: 300px;

			tr:first-child td {
				padding-top: 0.75rem;
			}

			td {
				padding: 0.3rem 0;

				&:last-child {
					text-align: right;
				}
			}

			.custom-invoice-row {
				&:hover {
					opacity: 0.7;
				}
				td {
					text-decoration: underline;
					cursor: pointer;
				}
			}

			.edit-row-btn-ctn {
				display: flex;
				justify-content: flex-end;
			}

			.save-btn-ctn {
				display: flex;
			}

			.col-with-del {
				display: flex;

				.icon {
					cursor: pointer;
				}
			}
		}

		tfoot {
			tr:first-child td {
				/** spacer since tfoot cannot have padding/margin itself */
				height: 15vh;
			}

			.total-row {
				td {
					border-top: 1px solid black;
					border-bottom: 1px solid black;
					font-size: 0.9rem;
					padding: 0.45rem 0;

					> div {
						display: flex;
						justify-content: space-between;
						align-items: center;
					}

					.to-pay {
						width: 250px;
						text-align: right;
						font-weight: ${(props) => props.theme.fontWeight.FONT_WEIGHT_MEDIUM};

						.total-value {
							font-size: 1.3rem;
						}
					}
				}
			}
		}

		.description {
			width: 400px;
		}
	}
`;

function formatPrice(num: number | undefined, showCurrency: boolean) {
	if (num === undefined) {
		return "";
	}

	return formatCurrencyString(num, { maximumFractionDigits: 2, hideCurrency: !showCurrency });
}

function createdEditableRowFromInvoiceRow(row: InvoiceRow, index: number): EditableRow {
	return {
		description: row.description,
		priceExcludingVAT: row.priceExcludingVAT + "",
		quantity: row.quantity + "",
		vatPercentage: row.vatPercentage || 0,
		rowIndex: index,
		originalRow: row,
		editing: false,
	};
}
